// ?reset=1 recovery mode, once per tab (job #387).
// window.name is the only per-tab store the wipe does not touch, so it carries
// "this tab has already been wiped" across reloads. Without it a reload mid-reset
// re-ran the wipe, erasing the one-reload-per-session guards in sessionStorage,
// and WebKit reloaded for ever.
export const RESET_MARK = 'ssi-reset-wiped'

export interface ResetDeps {
  win: Window
  unregisterAllServiceWorkers: () => Promise<unknown>
  clearAllCaches: () => Promise<unknown>
  deadlineMs?: number
}

export function runResetRecovery(d: ResetDeps): void {
  const { win } = d
  if (new URLSearchParams(win.location.search).get('reset') === '1') {
    const cleanUrl = new URL(win.location.href)
    cleanUrl.searchParams.delete('reset')

    if (win.name === RESET_MARK) {
      console.log('[App] Recovery mode - this tab was already wiped, not wiping again')
      win.location.replace(cleanUrl.toString())
      return
    }
    win.name = RESET_MARK
    console.log('[App] Recovery mode - clearing all data...')
    // Blocked storage must not prevent the remaining cleanup or the redirect.
    try { localStorage.clear() } catch { /* best-effort */ }
    try { sessionStorage.clear() } catch { /* best-effort */ }
    try {
      if (win.indexedDB) {
        win.indexedDB.databases?.().then(dbs => {
          dbs.forEach(db => {
            if (db.name) win.indexedDB.deleteDatabase(db.name)
          })
        }).catch(() => {})
      }
    } catch { /* blocked IndexedDB must not prevent cleanup or redirect */ }
    // Raced against a deadline: those promises can never settle on iOS.
    let deadline: ReturnType<typeof setTimeout> | undefined
    Promise.race([
      Promise.all([
        d.unregisterAllServiceWorkers().catch(() => {}),
        d.clearAllCaches().catch(() => {}),
      ]),
      new Promise(resolve => { deadline = setTimeout(resolve, d.deadlineMs ?? 4000) }),
    ]).finally(() => {
      clearTimeout(deadline)
      win.location.replace(cleanUrl.toString())
    })
  } else if (win.name === RESET_MARK) {
    // Reached the clean address: the next ?reset=1 in this tab is a fresh request.
    win.name = ''
  }
}
